/**
 * Resolve the current storefront slug for shopper-side navigation.
 *
 * PR-R5 moves shopper routes from flat paths (`/orders`, `/cart`, ...)
 * to nested `/store/:slug/*` paths. Components that build URLs
 * programmatically need a single source of truth for what `:slug`
 * should be in the current context.
 *
 * Resolution order:
 *   1. `route.params.slug` — present on the new restructured routes
 *      (`/store/:slug/orders`, `/store/:slug/cart`, etc.)
 *   2. `route.params.id` — present on `/store/:id` (the entry route,
 *      which keeps `:id` to preserve the redirect from `/shop/:id`).
 *   3. `localStorage.storeUrl` — set the first time the shopper visits
 *      `/store/:id` (preserved from the legacy storefront).
 *
 * Returns `null` when nothing resolves. Callers should fall back to
 * `/login` (for protected routes) or `/shop` (for the public landing).
 *
 * Empty-string params are treated as absent so a stale empty
 * `:slug` on an outgoing route doesn't shadow a still-valid value in
 * localStorage.
 *
 * Behavioural tests: `__tests__/storefront-slug-resolver.spec.js`.
 */

function nonEmpty(value) {
    return typeof value === "string" && value.length > 0 ? value : null;
}

export function resolveStorefrontSlug(route) {
    const fromSlug = nonEmpty(route?.params?.slug);
    if (fromSlug) return fromSlug;

    const fromId = nonEmpty(route?.params?.id);
    if (fromId) return fromId;

    if (typeof localStorage !== "undefined") {
        const fromLs = nonEmpty(localStorage.getItem("storeUrl"));
        if (fromLs) return fromLs;
    }

    return null;
}

/**
 * The store slug a storefront PATH belongs to (`/shop/<slug>/...` or the
 * legacy `/store/<slug>/...`), or null for any other path.
 *
 * For code that has a path but no route params -- the auth interceptor,
 * deciding where an expired anonymous visitor should land. Deliberately does
 * NOT fall back to `localStorage.storeUrl`: off the storefront there is no
 * store to send anyone to.
 */
export function storefrontSlugFromPath(path) {
    if (typeof path !== "string") return null;
    const match = /^\/(?:shop|store)\/([^/?#]+)/.exec(path);
    if (!match) return null;
    try {
        return nonEmpty(decodeURIComponent(match[1]));
    } catch {
        return null;
    }
}

/**
 * The store the visitor is on RIGHT NOW, from the address bar.
 *
 * The one answer to "is this a storefront page, and which store?" for code
 * that reacts to a request failing -- the auth interceptor and raiseError's
 * 401 branch. Read from `window.location`, not vue-router: during the INITIAL
 * navigation of a cold load the router still reports its start location `/`,
 * which hid the store from the interceptor and sent an anonymous visitor
 * reloading their cart to /login (gate bar-raiser P1-1 on #3957). The address
 * bar already names the destination.
 */
export function currentStorefrontSlug() {
    if (typeof window === "undefined" || !window.location) return null;
    return storefrontSlugFromPath(window.location.pathname);
}

/**
 * Where a shopper whose CREDENTIALED session ended on a storefront page goes:
 * the store's home, never the generic `/login` (Jake, 2026-10-02 -- a shopper
 * is never sent to the ThreadFlow sign-in, and there is no storefront
 * credential sign-in for the event). The one answer for raiseError's 401
 * branch; the auth interceptor reaches the same path through its
 * `navigateToStoreHome` (a full page load).
 */
export function storefrontHomePath(slug) {
    return `/shop/${encodeURIComponent(slug)}`;
}

export default resolveStorefrontSlug;
